/*
 * Purpose: Assemble OutcomePlan → Workflow graph with START/GOAL anchors + layout
 * Last modified: 2026-06-27 | Agent
 * Completeness: 92
 */
import type { Edge, Node, Workflow } from '@/stores/workflowStore';
import { createMissionGoalNode, createMissionStartNode } from '@/lib/missionBlocks';
import { createJourneyTubeNode } from '@/lib/journeyTube';
import { AVERAGE_USER_TEMPLATES } from '@/services/averageUserTemplates';
import { DEFAULT_JOB_GMAIL_QUERY } from '@/services/connectors/gmailSource';
import type { OutcomePlan } from '@/services/autoblocks/types';

const genId = () => Math.random().toString(36).slice(2, 11);

const X_START = 40;
const Y_START = 120;
const X_GAP = 220;

function cloneTemplateWorkflow(templateKey: keyof typeof AVERAGE_USER_TEMPLATES, plan: OutcomePlan): Workflow {
  const tpl = AVERAGE_USER_TEMPLATES[templateKey];
  const now = new Date();
  const idMap = new Map<string, string>();

  const nodes: Node[] = tpl.nodes.map((node) => {
    const newId = genId();
    idMap.set(node.id, newId);
    return {
      ...node,
      id: newId,
      config: {
        ...node.config,
        _autoBlocksStepId: node.id,
        _autoBlocksFromTemplate: templateKey,
      },
    };
  });

  const edges: Edge[] = tpl.edges.map((edge) => ({
    id: genId(),
    from: idMap.get(edge.from) ?? edge.from,
    to: idMap.get(edge.to) ?? edge.to,
  }));

  return {
    id: genId(),
    name: `AutoBlocks: ${tpl.name}`,
    nodes,
    edges,
    createdAt: now,
    updatedAt: now,
    missionMeta: { allowDynamicMap: true, mapLocked: false },
  };
}

function applyGmailInboundToWorkflow(workflow: Workflow, plan: OutcomePlan): Workflow {
  const wantsGmail =
    Boolean(plan.gmailInboxId) ||
    plan.useGmailInbound ||
    plan.steps.some((s) => s.subtype === 'gmail_inbound');
  if (!wantsGmail) return workflow;

  const triggerStep = plan.steps.find(
    (s) => s.type === 'trigger' && (s.subtype === 'gmail_inbound' || s.subtype === 'webhook')
  );
  const plannedSource = triggerStep?.config?.source as string | undefined;
  const plannedQuery = triggerStep?.config?.gmailQuery as string | undefined;

  const nodes = workflow.nodes.map((node) => {
    if (node.type !== 'trigger') return node;
    if (node.subtype !== 'webhook' && node.subtype !== 'gmail_inbound') return node;

    const source =
      plannedSource ||
      (plan.gmailInboxId ? 'webhook' : 'gmail_api');

    return {
      ...node,
      subtype: 'gmail_inbound' as const,
      config: {
        ...node.config,
        name: node.config.name || 'Gmail email received',
        inboxId: source === 'webhook' ? plan.gmailInboxId || node.config.inboxId : undefined,
        postUrl: source === 'webhook' ? plan.gmailPostUrl || node.config.postUrl : undefined,
        source,
        gmailQuery: plannedQuery || node.config.gmailQuery || DEFAULT_JOB_GMAIL_QUERY,
      },
    };
  });

  return { ...workflow, nodes };
}

export function assembleWorkflowFromPlan(plan: OutcomePlan, name?: string): Workflow {
  let workflow: Workflow;

  if (plan.matchedTemplateId && plan.matchedTemplateId in AVERAGE_USER_TEMPLATES) {
    workflow = cloneTemplateWorkflow(
      plan.matchedTemplateId as keyof typeof AVERAGE_USER_TEMPLATES,
      plan
    );
    if (name) workflow = { ...workflow, name };
  } else {
    const now = new Date();
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const nodeIdByStep = new Map<string, string>();

    plan.steps.forEach((step, index) => {
      const nodeId = genId();
      nodeIdByStep.set(step.id, nodeId);
      nodes.push({
        id: nodeId,
        type: step.type as Node['type'],
        subtype: step.subtype as Node['subtype'],
        config: { ...step.config, _autoBlocksStepId: step.id, _autoBlocksRationale: step.rationale },
        x: X_START + index * X_GAP,
        y: Y_START,
      });
    });

    for (let i = 0; i < plan.steps.length - 1; i++) {
      const from = nodeIdByStep.get(plan.steps[i].id);
      const to = nodeIdByStep.get(plan.steps[i + 1].id);
      if (from && to) {
        edges.push({ id: genId(), from, to });
      }
    }

    workflow = {
      id: genId(),
      name: name ?? `AutoBlocks: ${truncate(plan.interpretedGoal, 48)}`,
      nodes,
      edges,
      createdAt: now,
      updatedAt: now,
      missionMeta: { allowDynamicMap: true, mapLocked: false },
    };
  }

  return applyGmailInboundToWorkflow(applyJourneyTubeIfNeeded(ensureMissionAnchors(workflow, plan), plan), plan);
}

function applyJourneyTubeIfNeeded(workflow: Workflow, plan: OutcomePlan): Workflow {
  if (plan.director?.pacing !== 'journey_tube') {
    return workflow;
  }

  const start = workflow.nodes.find((n) => n.type === 'start');
  const goal = workflow.nodes.find((n) => n.type === 'goal');
  if (!start || !goal) return workflow;

  const phases = plan.director.journeyPhases ?? ['Let the outcome unfold', 'Attach blocks when ready'];
  const tube = createJourneyTubeNode(plan.id, phases, X_START + X_GAP, Y_START);

  const nodes = [
    ...workflow.nodes.filter((n) => n.type !== 'tube'),
    { ...tube, config: { ...tube.config, _autoBlocksDirector: plan.director } },
  ];

  const withoutDirectStartGoal = workflow.edges.filter(
    (e) => !(e.from === start.id && e.to === goal.id)
  );

  const edges = [
    ...withoutDirectStartGoal.filter((e) => e.from !== start.id || e.to !== goal.id),
    { id: genId(), from: start.id, to: tube.id, kind: 'journey_tube' as const },
    { id: genId(), from: tube.id, to: goal.id, kind: 'journey_tube' as const },
  ];

  const operational = workflow.nodes.filter(
    (n) => n.type === 'trigger' || n.type === 'tool' || n.type === 'action'
  );
  if (operational[0]) {
    edges.push({ id: genId(), from: tube.id, to: operational[0].id, kind: 'link' });
  }

  return { ...workflow, nodes, edges };
}

function ensureMissionAnchors(workflow: Workflow, plan: OutcomePlan): Workflow {
  const hasStart = workflow.nodes.some((n) => n.type === 'start');
  const outcome = (plan.interpretedGoal || plan.rawOutcome || '').trim();

  const operational = workflow.nodes.filter(
    (n) => n.type === 'trigger' || n.type === 'tool' || n.type === 'action'
  );

  const startNode = hasStart
    ? workflow.nodes.find((n) => n.type === 'start')!
    : createMissionStartNode(X_START, Y_START - 160);

  const goalNode =
    outcome && !workflow.nodes.some((n) => n.type === 'goal')
      ? {
          ...createMissionGoalNode(outcome, X_START, Y_START - 80),
          config: {
            outcome,
            name: 'Identified outcome',
            _autoBlocksFromPlan: plan.id,
          },
        }
      : null;

  const anchorNodes: Node[] = [];
  if (!hasStart) anchorNodes.push(startNode);
  if (goalNode) anchorNodes.push(goalNode);

  const shiftedOperational = operational.map((node, index) => ({
    ...node,
    x: X_START + (anchorNodes.length + index) * X_GAP,
    y: Y_START,
  }));

  const otherNodes = workflow.nodes.filter(
    (n) => n.type !== 'trigger' && n.type !== 'tool' && n.type !== 'action'
  );

  const nodes = [...otherNodes, ...anchorNodes, ...shiftedOperational];
  const edges = [...workflow.edges];

  const firstOperational = shiftedOperational[0];
  let chainTail = startNode.id;

  if (goalNode) {
    if (!edges.some((e) => e.from === startNode.id && e.to === goalNode.id)) {
      edges.unshift({ id: genId(), from: startNode.id, to: goalNode.id });
    }
    chainTail = goalNode.id;
  }

  if (firstOperational && !edges.some((e) => e.from === chainTail && e.to === firstOperational.id)) {
    edges.unshift({ id: genId(), from: chainTail, to: firstOperational.id });
  }

  return { ...workflow, nodes, edges };
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

export function attachWorkflowPreview(plan: OutcomePlan): OutcomePlan {
  const workflow = assembleWorkflowFromPlan(plan);
  return {
    ...plan,
    workflowPreview: {
      name: workflow.name,
      nodes: workflow.nodes,
      edges: workflow.edges,
    },
  };
}
